// frontend/src/pages/BulkAnalysisPage/SnippetModal.jsx

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const SnippetModal = ({ isOpen, onClose, snippet, type = 'html', title }) => {
    const { t } = useTranslation();
    const [copySuccess, setCopySuccess] = useState(false);

    if (!isOpen) return null;

    const handleCopy = () => {
        navigator.clipboard.writeText(snippet).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    const handleTest = () => {
        if (!snippet) return;

        const isVast = type === 'vast';

        if (isVast) {
            // --- THIS IS THE VAST TESTER LOGIC COPIED FROM AdCreativeTools.jsx ---
            const wrapperXmlString = snippet;
            const backendBaseUrl = window.location.origin;

            const vastTesterHtml = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Live VAST Preview</title>
                <style>
                    body { font-family: sans-serif; background-color: #f0f2f5; margin: 0; padding: 40px; text-align: center; }
                    #mainContainer { width: 640px; height: 360px; margin: 20px auto; border: 2px dashed #ccc; background-color: black; box-shadow: 0 4px 8px rgba(0,0,0,0.1); position: relative; }
                    #contentElement { width: 100%; height: 100%; background-color: black; }
                    #adContainer { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
                    .overlay-ui { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10; }
                    .overlay-ui button { padding: 10px 20px; font-size: 16px; cursor: pointer; border: 2px solid white; background-color: rgba(0,0,0,0.7); color: white; border-radius: 5px; }
                    .overlay-ui button:hover { background-color: rgba(0,0,0,0.9); }
                    #loadingIndicator { display: none; width: 80px; height: 80px; border: 8px solid rgba(255, 255, 255, 0.2); border-top-color: white; border-radius: 50%; animation: spin 1s linear infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    #errorLog { max-width: 640px; margin: 20px auto; padding: 15px; background-color: #ffebee; border: 1px solid #c62828; color: #c62828; text-align: left; white-space: pre-wrap; word-wrap: break-word; display: none; box-sizing: border-box; }
                    #debugLog { max-width: 640px; margin: 20px auto; padding: 15px; background-color: #e3f2fd; border: 1px solid #1976d2; color: #1976d2; text-align: left; white-space: pre-wrap; word-wrap: break-word; font-size: 12px; box-sizing: border-box; }
                </style>
                <script src="//imasdk.googleapis.com/js/sdkloader/ima3.js"></script>
            </head>
            <body>
                <h1>Live VAST Preview</h1>
                <div id="mainContainer"><video id="contentElement" playsinline controlslist="nodownload"></video><div id="adContainer"></div><div id="uiContainer" class="overlay-ui"><div id="loadingIndicator"></div><button id="playButton">Play Ad</button></div></div>
                <div id="errorLog"></div><div id="debugLog"></div>
                <script>
                    const backendOutputXml = \`${wrapperXmlString.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
                    let adDisplayContainer, adsLoader, adsManager;
                    const videoElement = document.getElementById('contentElement');
                    const adContainer = document.getElementById('adContainer');
                    const playButton = document.getElementById('playButton');
                    const loadingIndicator = document.getElementById('loadingIndicator');
                    const errorLog = document.getElementById('errorLog');
                    const debugLog = document.getElementById('debugLog');
                    function log(message) { debugLog.textContent += message + '\\n'; }
                    function isWrapper(xmlString) { return xmlString.includes("<VASTAdTagURI>"); }
                    async function playAd() {
                        log('--- New Ad Request ---');
                        playButton.style.display = 'none';
                        loadingIndicator.style.display = 'block';
                        errorLog.style.display = 'none';
                        videoElement.pause(); videoElement.src = ''; videoElement.load();
                        try {
                            let finalVastForIma;
                            const isWrapperResponse = isWrapper(backendOutputXml);
                            log(isWrapperResponse ? 'Detected VAST Wrapper response.' : 'Detected VAST Inline response.');
                            if (isWrapperResponse) {
                                const parser = new DOMParser();
                                const wrapperDoc = parser.parseFromString(backendOutputXml, "text/xml");
                                const vastAdTagURI = wrapperDoc.querySelector("VASTAdTagURI")?.textContent.trim();
                                const iconsNode = wrapperDoc.querySelector("Icons");
                                if (!vastAdTagURI) throw new Error("Invalid wrapper: Missing VASTAdTagURI");
                                const proxyUrl = \`${backendBaseUrl}/api/proxy/file\`;
                                const response = await fetch(proxyUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: vastAdTagURI, contentType: 'text' }) });
                                if (!response.ok) { const errData = await response.json(); throw new Error(\`Failed to fetch downstream VAST: \${errData.error || response.statusText}\`); }
                                const finalVastString = await response.text();
                                const finalVastDoc = parser.parseFromString(finalVastString, "text/xml");
                                const linearNode = finalVastDoc.querySelector("Linear");
                                if (linearNode && iconsNode) { linearNode.prepend(iconsNode.cloneNode(true)); }
                                finalVastForIma = new XMLSerializer().serializeToString(finalVastDoc);
                                log('Final stitched VAST ready for IMA.');
                            } else {
                                finalVastForIma = backendOutputXml;
                                log('Using Inline VAST directly for IMA.');
                            }
                            adDisplayContainer = new google.ima.AdDisplayContainer(adContainer, videoElement);
                            adDisplayContainer.initialize();
                            adsLoader = new google.ima.AdsLoader(adDisplayContainer);
                            adsLoader.addEventListener(google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, onAdsManagerLoaded, false);
                            adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);
                            const adsRequest = new google.ima.AdsRequest();
                            adsRequest.adsResponse = finalVastForIma;
                            adsRequest.linearAdSlotWidth = 640;
                            adsRequest.linearAdSlotHeight = 360;
                            adsLoader.requestAds(adsRequest);
                        } catch (error) { onAdError({ getError: () => error }); }
                    }
                    function onAdsManagerLoaded(e) {
                        log('Ads manager loaded.');
                        loadingIndicator.style.display = 'none';
                        const adsRenderingSettings = new google.ima.AdsRenderingSettings();
                        adsRenderingSettings.useStyledLinearAds = true;
                        adsManager = e.getAdsManager(videoElement, adsRenderingSettings);
                        adsManager.addEventListener(google.ima.AdEvent.Type.LOADED, () => adsManager.setVolume(1));
                        adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
                        adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, onAllAdsCompleted);
                        try { adsManager.init(640, 360, google.ima.ViewMode.NORMAL); adsManager.start(); } catch (adError) { onAdError({ getError: () => adError }); }
                    }
                    function onAllAdsCompleted() {
                        log('All ads completed.');
                        cleanupIma();
                        playButton.textContent = 'Replay Ad';
                        playButton.style.display = 'block';
                    }
                    function onAdError(e) {
                        const error = e ? e.getError() : null;
                        let msg = 'Ad Error: ' + (error ? (error.getMessage ? error.getMessage() : error.toString()) : 'Unknown Error');
                        log('ERROR: ' + msg);
                        errorLog.textContent = msg;
                        errorLog.style.display = 'block';
                        loadingIndicator.style.display = 'none';
                        playButton.textContent = 'Retry';
                        playButton.style.display = 'block';
                        cleanupIma();
                    }
                    function cleanupIma() {
                        if (adsManager) { adsManager.destroy(); adsManager = null; }
                        if (adsLoader) { adsLoader.contentComplete(); adsLoader = null; }
                        if (adDisplayContainer) { adDisplayContainer.destroy(); adDisplayContainer = null; }
                    }
                    playButton.addEventListener('click', playAd);
                    log('VAST Tester ready.');
                </script>
            </body>
            </html>`;
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.open();
                newWindow.document.write(vastTesterHtml);
                newWindow.document.close();
            } else {
                alert(t('adCreativeTools.snippetModal.popupWarning'));
            }
        } else {
            // --- THIS IS THE ORIGINAL HTML SNIPPET TESTER LOGIC ---
            const htmlContent = `
            <!DOCTYPE html><html lang="en"><head><title>${t('adCreativeTools.snippetModal.testTitle')}</title><style>body{font-family:sans-serif;background-color:#f0f2f5;padding:40px;text-align:center}.ad-wrapper{max-width:800px;margin:20px auto;border:2px dashed #ccc;padding:10px;background-color:white;box-shadow:0 4px 8px rgba(0,0,0,0.1);display:inline-block}</style></head><body><h1>${t('adCreativeTools.snippetModal.livePreview')}</h1><div class="ad-wrapper">${snippet}</div></body></html>`;
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.open();
                newWindow.document.write(htmlContent);
                newWindow.document.close();
            } else {
                alert(t('adCreativeTools.snippetModal.popupWarning'));
            }
        }
    };

    const isVast = type === 'vast';

    return ReactDOM.createPortal(
        <div className="confirmation-dialog-overlay open" onClick={onClose} role="dialog" aria-modal="true">
            <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                <IconButton aria-label="close" onClick={onClose} className="dialog-close-button"><CloseIcon /></IconButton>
                <h2 className="confirmation-dialog-title">{title || (isVast ? 'VAST XML Snippet' : t('adCreativeTools.snippetArea.title'))}</h2>
                <div className="confirmation-dialog-body" style={{ position: 'relative' }}>
                    <Tooltip title={copySuccess ? t('adCreativeTools.snippetArea.copySuccess') : t('adCreativeTools.snippetArea.copy')} placement="top">
                        <IconButton onClick={handleCopy} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.08)' } }}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                    <div style={{ maxHeight: '300px', overflowY: 'auto', background: '#1e1e1e', borderRadius: '4px', padding: '1em' }}>
                        <SyntaxHighlighter language={isVast ? 'xml' : 'html'} style={vscDarkPlus} customStyle={{ margin: 0, padding: 0, background: 'transparent', overflow: 'visible', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {snippet}
                        </SyntaxHighlighter>
                    </div>
                </div>
                <div className="confirmation-dialog-actions">
                    <Button onClick={onClose} variant="outlined">{t('bulkAnalysisPage.dialogs.common.cancel')}</Button>
                    <Button onClick={handleTest} variant="contained" color="secondary" startIcon={<OpenInNewIcon />}>{t('adCreativeTools.snippetArea.testButton')}</Button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SnippetModal;